import React from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';

const AwardsSection = () => {
  const awards = [
    {
      year: "2023",
      title: "Prix d'Excellence Corporative RDC",
      organization: "Chambre de Commerce RDC",
      description: "Reconnu pour sa contribution exceptionnelle au développement commercial et à l'innovation de services en République Démocratique du Congo.",
      icon: "Trophy",
      category: "Excellence Commerciale"
    },
    {
      year: "2023",
      title: "Meilleur Fournisseur Multi-Services",
      organization: "Prix d'Affaires d'Afrique Centrale",
      description: "Récompensé pour la prestation de services complète dans plusieurs secteurs d'activité avec une qualité constante et une satisfaction client.",
      icon: "Award",
      category: "Excellence de Service"
    },
    {
      year: "2022",
      title: "Innovation dans l'Immobilier",
      organization: "Association Immobilière RDC",
      description: "Honoré pour la mise en œuvre de technologies de pointe et de pratiques transparentes dans le secteur immobilier de la RDC.",
      icon: "Building2",
      category: "Innovation"
    },
    {
      year: "2022",
      title: "Employeur de Choix",
      organization: "Forum d'Excellence RH RDC",
      description: "Reconnu pour la création d'une culture de travail exceptionnelle, des programmes de développement des employés et l'autonomisation des talents locaux.",
      icon: "Users",
      category: "Ressources Humaines"
    },
    {
      year: "2021",
      title: "Leadership en Durabilité",
      organization: "Conseil Environnemental d'Afrique Centrale",
      description: "Reconnu pour l'engagement envers la responsabilité environnementale et les pratiques commerciales durables dans toutes les opérations.",
      icon: "Leaf",
      category: "Durabilité"
    },
    {
      year: "2021",
      title: "Excellence du Service Client",
      organization: "Institut International de Qualité de Service",
      description: "Récompensé pour le maintien d'un taux de fidélisation client de 98% et une satisfaction client exceptionnelle dans toutes les catégories de services.",
      icon: "Star",
      category: "Service Client"
    },
    {
      year: "2020",
      title: "Leader de la Transformation Numérique",
      organization: "Forum Technologique RDC",
      description: "Reconnu pour la mise en œuvre réussie de solutions numériques complètes et l'avancement de l'adoption technologique sur le marché de la RDC.",
      icon: "Laptop",
      category: "Technologie"
    },
    {
      year: "2019",
      title: "Prix d'Impact Communautaire",
      organization: "Alliance de Responsabilité Sociale RDC",
      description: "Honoré pour les contributions significatives au développement communautaire, à l'éducation et aux initiatives d'autonomisation économique locale.",
      icon: "Heart",
      category: "Impact Social"
    }
  ];

  return (
    <section className="py-20 lg:py-28 bg-slate-100 dark:bg-slate-900 border-t border-slate-200 dark:border-white/5 relative overflow-hidden">
      {/* Noise texture overlay */}
      <div
        className="absolute inset-0 opacity-[0.03] pointer-events-none"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
          backgroundRepeat: 'repeat',
        }}
      />

      {/* Animated blob backgrounds */}
      <motion.div
        className="absolute -top-40 -left-40 w-[500px] h-[500px] bg-blue-600/10 rounded-full blur-[120px]"
        animate={{ x: [0, 60, 0], y: [0, 40, 0], scale: [1, 1.15, 1] }}
        transition={{ duration: 18, repeat: Infinity, ease: 'easeInOut' }}
      />
      <motion.div
        className="absolute -bottom-40 -right-40 w-[500px] h-[500px] bg-amber-500/10 rounded-full blur-[120px]"
        animate={{ x: [0, -50, 0], y: [0, -30, 0], scale: [1, 1.2, 1] }}
        transition={{ duration: 22, repeat: Infinity, ease: 'easeInOut' }}
      />

      <div className="w-full max-w-[1920px] mx-auto px-6 sm:px-12 lg:px-16 relative z-10">
        {/* Header */}
        <motion.div
          className="text-center mb-12 lg:mb-20"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.7 }}
        >
          <div className="inline-flex items-center gap-2 bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 px-5 py-2.5 rounded-full mb-6">
            <Icon name="Medal" size={18} className="text-amber-400" />
            <span className="text-sm font-medium text-slate-600 dark:text-slate-300">Prix & Reconnaissance</span>
          </div>

          <h2 className="text-4xl md:text-6xl font-bold text-slate-900 dark:text-white mb-5">
            Célébrer l'Excellence &{' '}
            <span className="bg-gradient-to-r from-blue-400 to-amber-400 bg-clip-text text-transparent">
              les Réalisations
            </span>
          </h2>

          <p className="text-xl text-slate-600 dark:text-slate-400 max-w-3xl mx-auto leading-relaxed">
            Notre engagement envers l'excellence a été reconnu par les principales organisations et institutions de l'industrie à travers la République Démocratique du Congo et l'Afrique Centrale.
          </p>
        </motion.div>

        {/* Awards grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          {awards?.map((award, index) => (
            <motion.div
              key={index}
              className="bg-white/80 dark:bg-slate-800/40 backdrop-blur-xl border border-slate-200 dark:border-white/10 rounded-3xl p-8 md:p-10 hover:border-blue-500/30 shadow-2xl group transition-colors duration-300"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.5, delay: index * 0.08 }}
            >
              <div className="flex items-start gap-4 mb-4">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-500 to-amber-700 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                  <Icon name={award?.icon} size={28} className="text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-xl font-bold text-amber-400">
                      {award?.year}
                    </span>
                    <span className="text-xs px-3 py-1 bg-blue-500/10 text-blue-400 rounded-full font-medium">
                      {award?.category}
                    </span>
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 dark:text-white">
                    {award?.title}
                  </h3>
                </div>
              </div>

              <p className="text-slate-600 dark:text-slate-400 leading-relaxed mb-4">
                {award?.description}
              </p>

              <div className="pt-4 border-t border-slate-200 dark:border-slate-700/50 flex items-center gap-2">
                <Icon name="Building" size={16} className="text-slate-500" />
                <span className="text-sm text-slate-500 line-clamp-1">{award?.organization}</span>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Bottom stats banner */}
        <motion.div
          className="mt-16 lg:mt-24"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ duration: 0.7, delay: 0.2 }}
        >
          <div className="bg-gradient-to-r from-blue-900/20 to-slate-800/40 backdrop-blur-xl border border-white/5 rounded-[2.5rem] p-12 md:p-20">
            <div className="flex flex-col md:flex-row items-center justify-center gap-10 md:gap-16">
              <div className="flex items-center gap-4">
                <Icon name="TrendingUp" size={40} className="text-amber-400" />
                <div className="text-left">
                  <div className="text-4xl md:text-5xl font-bold text-slate-900 dark:text-white">8+</div>
                  <div className="text-sm text-slate-600 dark:text-slate-400">Prix de l'Industrie</div>
                </div>
              </div>

              <div className="hidden md:block w-px h-16 bg-slate-200 dark:bg-white/10" />

              <div className="flex items-center gap-4">
                <Icon name="Star" size={40} className="text-amber-400" />
                <div className="text-left">
                  <div className="text-4xl md:text-5xl font-bold text-slate-900 dark:text-white">98%</div>
                  <div className="text-sm text-slate-600 dark:text-slate-400">Satisfaction Client</div>
                </div>
              </div>

              <div className="hidden md:block w-px h-16 bg-slate-200 dark:bg-white/10" />

              <div className="flex items-center gap-4">
                <Icon name="Users" size={40} className="text-amber-400" />
                <div className="text-left">
                  <div className="text-4xl md:text-5xl font-bold text-slate-900 dark:text-white">500+</div>
                  <div className="text-sm text-slate-600 dark:text-slate-400">Clients Satisfaits</div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default AwardsSection;
